import { useMutation, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Download, Package } from 'lucide-react';
import { useState } from 'react';
import { CopyButton } from '@/components/shared/copy-button';
import { EmptyState } from '@/components/shared/empty-state';
import { TableSkeleton } from '@/components/shared/loading';
import { LoadingButton } from '@/components/shared/loading-button';
import { SectionCard } from '@/components/shared/section-card';
import { ToneBadge } from '@/components/shared/tone-badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/lib/api';
import { fmtDate } from '@/lib/format';
import type { SdkRelease } from '@/lib/types';

const fmtSize = (bytes: number) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

/**
 * Server SDK downloads (P5.17). The files are private in S3 or Spaces; each click asks the API for a
 * fresh 5-minute presigned URL, so a copied link stops working soon after.
 */
export function ServerSdkCard() {
  const [showOlder, setShowOlder] = useState(false);
  const releases = useQuery({
    queryKey: ['sdk-releases', 'server-sdk'],
    queryFn: () => api<{ data: SdkRelease[]; download_enabled: boolean }>('/admin/v1/sdk-releases', { query: { package: 'server-sdk' } }),
  });
  const download = useMutation({
    mutationFn: (id: string) => api<{ url: string }>(`/admin/v1/sdk-releases/${id}/download-url`),
    // The URL answers with Content-Disposition: attachment, so the browser downloads and stays here.
    onSuccess: ({ url }) => window.location.assign(url),
  });

  const [latest, ...older] = releases.data?.data ?? [];
  const downloadButton = (r: SdkRelease, label: string, variant: 'default' | 'outline' = 'outline') => (
    <LoadingButton size="sm" variant={variant} loading={download.isPending && download.variables === r.id} onClick={() => download.mutate(r.id)}>
      <Download /> {label}
    </LoadingButton>
  );

  return (
    <SectionCard
      title="Server SDK"
      description="Node.js package for sending notifications from your backend with a server key."
      flush={!latest || !releases.data?.download_enabled}
    >
      {releases.isLoading ? <TableSkeleton rows={2} /> : releases.data && !releases.data.download_enabled ? (
        // Without object storage nothing is published and no download URL can be signed.
        <EmptyState icon={Package} title="SDK downloads are off" hint="The API has no object storage configured (S3_BUCKET or SPACES_BUCKET and its keys in the server's .env)." />
      ) : !latest ? (
        <EmptyState icon={Package} title="No SDK release published yet" hint="The API publishes the SDK bundled with each deploy when it starts." />
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-medium">{latest.file_name}</span>
                <ToneBadge tone="green">latest</ToneBadge>
              </div>
              <p className="text-xs text-muted-foreground">
                v{latest.version} · {fmtSize(latest.size_bytes)} · built {fmtDate(latest.built_at)}
              </p>
            </div>
            {downloadButton(latest, `Download v${latest.version}`, 'default')}
          </div>

          {latest.release_notes && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm whitespace-pre-wrap">{latest.release_notes}</div>
          )}

          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">Install it in your backend, then see the SDK README inside the package:</p>
            <div className="flex gap-2">
              <code className="flex-1 truncate rounded-md border bg-muted/40 px-3 py-2 font-mono">npm install ./{latest.file_name}</code>
              <CopyButton value={`npm install ./${latest.file_name}`} />
            </div>
            <p className="truncate font-mono text-muted-foreground" title={latest.sha256}>sha256 {latest.sha256}</p>
          </div>

          {older.length > 0 && (
            <div className="border-t pt-3">
              <Button variant="ghost" size="sm" className="-ml-2" onClick={() => setShowOlder((v) => !v)}>
                {showOlder ? <ChevronDown /> : <ChevronRight />} Older versions ({older.length})
              </Button>
              {showOlder && (
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Version</TableHead><TableHead>Built</TableHead><TableHead>Size</TableHead><TableHead /></TableRow>
                  </TableHeader>
                  <TableBody>
                    {older.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell className="font-mono">v{r.version}</TableCell>
                        <TableCell className="text-muted-foreground">{fmtDate(r.built_at)}</TableCell>
                        <TableCell className="text-muted-foreground">{fmtSize(r.size_bytes)}</TableCell>
                        <TableCell className="text-right">{downloadButton(r, 'Download')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}
